import React from 'react';
import './style.css';
import { useSelector, useDispatch } from 'react-redux';
import { search, setTabPanel, setSearchSelectedIndex, setForceSearchResults } from '../../../../redux/reducers/global/editor/editorSlice';
import { requestSpawnSymbol, clearSpawnPreview } from '../../../../redux/reducers/global/view/viewSlice';
import { EDITOR_TAB_PANEL_ELEMENTS, EDITOR_TAB_PANEL_INSPECTOR } from '../../../../utilities/constants/editorConstants';
import TabPanels from '../tabpanel/TabPanels';
import { selectInspectorEnabled } from './selectors/sidebarSelectors';
import { DEFAULT_ICON_SCALE_CM } from '../../../../utilities/dragHelpers';


function SearchBar({ onSearchInput, searchQuery }) {
  const dispatch = useDispatch()
  const searchResults = useSelector((state) => state.editor.searchResults) || [];
  const angleOffset = useSelector((state) => state.editor.angleOffset);
  const currentQuery = useSelector((state) => state.editor.searchQuery) || '';
  const [isActive, setIsActive] = React.useState(false);
  const inputRef = React.useRef(null);

  const cloneDeep = React.useCallback((value) => {
    if (value === undefined || value === null) return value;
    return JSON.parse(JSON.stringify(value));
  }, []);

  const linearizeResults = React.useCallback(() => {
    if (!Array.isArray(searchResults) || searchResults.length === 0) return [];
    return searchResults;
  }, [searchResults]);

  const spawnResult = React.useCallback((result) => {
    if (!result) return;
    const toEditorPath = (src) => {
      if (!src || typeof src !== 'string') return src;
      if (src.includes('/assets/editor-symbols/')) return src;
      if (
        src === '/symbol-assets/Bus.svg' ||
        src === '/symbol-assets/BusMultinodo.svg'
      ) return src;
      return src.replace('/symbol-assets/', '/assets/editor-symbols/');
    };
    const variants = Array.isArray(result.variants)
      ? result.variants.map((variant) => ({ ...variant }))
      : undefined;
    if (variants) {
      variants.forEach((variant) => {
        if (variant.diagramSvgSrc) {
          variant.diagramSvgSrc = toEditorPath(variant.diagramSvgSrc);
        } else if (variant.svgSrc) {
          variant.diagramSvgSrc = toEditorPath(variant.svgSrc);
        }
      });
    }
    const payload = {
      alias: result.alias || result.baseAlias,
      baseAlias: result.baseAlias || result.alias,
      label: result.displayLabel || result.label,
      svgSrc: toEditorPath(result.diagramSvgSrc || result.svgSrc),
      angle: angleOffset,
      isRotable: !!result.rotable,
      symbolLabel: result.type,
      attributes: cloneDeep(result.attributes),
      handles: cloneDeep(result.handles),
      variants,
      variantAlias: result.variantAlias || null,
      scaleCm: DEFAULT_ICON_SCALE_CM,
      isSubdiagram: result.type === 'Subdiagram' ? true : false,
    };
    dispatch(clearSpawnPreview());
    dispatch(requestSpawnSymbol({ data: payload }));
    dispatch(search({ query: '' }));
    dispatch(setForceSearchResults(false));
    dispatch(setSearchSelectedIndex(0));
    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    setIsActive(false);
    inputRef.current?.blur();
  }, [angleOffset, cloneDeep, dispatch]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    onSearchInput(value);
    setIsActive(value.trim().length > 0);
  }

  const handleFocus = () => {
    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    setIsActive(true);
  }

  const handleBlur = () => {
    const value = inputRef.current ? inputRef.current.value : '';
    setIsActive(value.trim().length > 0);
  };

  const handleKeyDown = (e) => {
    const hasQuery = (currentQuery || '').trim().length > 0;
    if (/^[1-9]$/.test(e.key)) {
      if (!hasQuery) return; // ignore numeric selection with empty query
      const n = parseInt(e.key, 10) - 1;
      const linear = linearizeResults();
      const target = linear[n];
      if (target) {
        spawnResult(target);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Enter') {
      if (!hasQuery) { e.preventDefault(); return; }
      const linear = linearizeResults();
      const first = linear[0];
      if (first) {
        spawnResult(first);
        e.preventDefault();
      }
      return;
    }
  };

  return (
    <div className={`searchBarInputWrapper${isActive ? ' is-active' : ''}`}>
      <span className="searchBarInputIcon" aria-hidden="true" />
      <input
        className="searchBarInput"
        type="text"
        placeholder="Buscar..."
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        ref={inputRef}
      />
      <span className="searchBarEnterHint">Enter</span>
    </div>
  )
}

const TabButton = React.memo(({ isActive, onClick, children }) => (
  <button className={isActive ? 'tab active' : 'tab'} onClick={onClick}>
    {children}
  </button>
));

const Tabs = React.memo(() => {
  const dispatch = useDispatch();
  const tabPanel = useSelector((state) => state.editor.tabPanel);
  const inspectorEnabled = useSelector(selectInspectorEnabled);

  const handleTabClick = React.useCallback(
    (targetTab) => () => {
      if (targetTab === EDITOR_TAB_PANEL_INSPECTOR && !inspectorEnabled) {
        return;
      }
      dispatch(setTabPanel({ tabPanel: targetTab }));
      if (targetTab === EDITOR_TAB_PANEL_ELEMENTS) {
        dispatch(search({ query: '' }));
      }
    },
    [dispatch, inspectorEnabled]
  );

  return (
    <div className="tab-container">
      <div className={`tab-list${inspectorEnabled ? '' : ' tab-list--single'}`}>
        <TabButton
          isActive={tabPanel === EDITOR_TAB_PANEL_ELEMENTS}
          onClick={handleTabClick(EDITOR_TAB_PANEL_ELEMENTS)}
        >
          Componentes
        </TabButton>
        {inspectorEnabled ? (
          <TabButton
            isActive={tabPanel === EDITOR_TAB_PANEL_INSPECTOR}
            onClick={handleTabClick(EDITOR_TAB_PANEL_INSPECTOR)}
          >
            Características
          </TabButton>
        ) : null}
      </div>
      <div className="tab-content">
        <TabPanels/>
      </div>
    </div>
  );
});

export const Sidebar = () => {
  const dispatch = useDispatch();
  const searchQuery = useSelector((state) => state.editor.searchQuery);

  const onSearchInput = React.useCallback(
    (query) => {
      dispatch(search({ query }));
    },
    [dispatch]
  );

  return (
    <>
      <div className='search-rotatebtn'>
        <SearchBar onSearchInput={onSearchInput} searchQuery={searchQuery}/>
      </div>
      <Tabs />
    </>
  );
};

export default Sidebar;
